/**
 * VIP Tools Embedded in Advisor Dashboard
 * Dealers get direct access without ToolSalesGate
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calculator, Loader2, DollarSign, TrendingDown, BarChart3, PiggyBank, 
  Fuel, Shield, Wrench, Home, Tent, Gavel, Target, MessageSquare, 
  ThumbsUp, AlertTriangle, CheckCircle, Upload, Image, X, Search 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Cost Calculator for Dealers
 */
export const CostCalculatorEmbed = ({ isDark }) => {
  const [form, setForm] = useState({
    purchase_price: '', rv_type: 'travel_trailer', financing: false,
    down_payment: '', loan_term_years: '10', interest_rate: '6.5',
    years_to_calculate: '5', miles_per_year: '5000', storage_needed: false, full_time: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.purchase_price) { toast.error('Please enter a purchase price'); return; }
    setLoading(true);
    try {
      const payload = {
        purchase_price: parseFloat(form.purchase_price), rv_type: form.rv_type,
        financing: form.financing, down_payment: form.down_payment ? parseFloat(form.down_payment) : 0,
        loan_term_years: parseInt(form.loan_term_years), interest_rate: parseFloat(form.interest_rate),
        years_to_calculate: parseInt(form.years_to_calculate),
        miles_per_year: parseInt(form.miles_per_year),
        storage_needed: form.storage_needed, full_time: form.full_time,
      };
      const res = await axios.post(`${API}/tools/cost-calculator`, payload);
      setResult(res.data);
    } catch (err) {
      console.error('Cost calculator error:', err);
      toast.error(err.response?.data?.detail || 'Failed to calculate costs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-green-500/20">
          <Calculator className="text-green-500" size={28} />
        </div>
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            RV Cost of Ownership Calculator
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Calculate the true 5-year cost of RV ownership
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Purchase Price *</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-3 text-gray-400" size={16} />
              <Input
                type="number"
                placeholder="50000"
                value={form.purchase_price}
                onChange={(e) => setForm({...form, purchase_price: e.target.value})}
                className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
            </div>
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>RV Type</Label>
            <select
              value={form.rv_type}
              onChange={(e) => setForm({...form, rv_type: e.target.value})}
              className={`w-full mt-1 h-10 rounded-md border px-3 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
            >
              <option value="travel_trailer">Travel Trailer</option>
              <option value="fifth_wheel">Fifth Wheel</option>
              <option value="motorhome_class_a">Class A Motorhome</option>
              <option value="motorhome_class_b">Class B Motorhome</option>
              <option value="motorhome_class_c">Class C Motorhome</option>
            </select>
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Miles per Year</Label>
            <Input
              type="number"
              placeholder="5000"
              value={form.miles_per_year}
              onChange={(e) => setForm({...form, miles_per_year: e.target.value})}
              className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Years to Calculate</Label>
            <select
              value={form.years_to_calculate}
              onChange={(e) => setForm({...form, years_to_calculate: e.target.value})}
              className={`w-full mt-1 h-10 rounded-md border px-3 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
            >
              {[1,2,3,4,5,7,10].map(y => <option key={y} value={y}>{y} Year{y>1?'s':''}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-4 mt-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.financing}
                onChange={(e) => setForm({...form, financing: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <span className={isDark ? 'text-gray-300' : ''}>Financing</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.storage_needed}
                onChange={(e) => setForm({...form, storage_needed: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <span className={isDark ? 'text-gray-300' : ''}>Storage Needed</span>
            </label>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="mt-6 bg-green-600 hover:bg-green-700">
          {loading ? <><Loader2 className="animate-spin mr-2" size={18} />Calculating...</> : 'Calculate Costs'}
        </Button>
      </form>

      {result && (
        <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : ''}`}>
              {result.years_calculated}-Year Cost Summary
            </h2>
            <button
              onClick={() => setResult(null)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isDark 
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X size={14} />
              Clear
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-50'}`}>
              <p className="text-sm text-gray-500">Total Cost</p>
              <p className="text-2xl font-bold text-green-600">${result.total_cost?.toLocaleString()}</p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className="text-sm text-gray-500">Monthly Average</p>
              <p className="text-2xl font-bold text-blue-600">${result.monthly_average?.toLocaleString()}</p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-purple-50'}`}>
              <p className="text-sm text-gray-500">Depreciation</p>
              <p className="text-2xl font-bold text-purple-600">${result.total_depreciation?.toLocaleString()}</p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-orange-50'}`}>
              <p className="text-sm text-gray-500">End Value</p>
              <p className="text-2xl font-bold text-orange-600">${result.estimated_value_end?.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Deal Analyzer for Dealers - with Image Upload
 */
export const DealAnalyzerEmbed = ({ isDark }) => {
  const [mode, setMode] = useState('manual'); // 'manual' or 'image'
  const [form, setForm] = useState({
    asking_price: '', rv_year: '', rv_make: '', rv_model: '',
    rv_type: 'travel_trailer', condition: 'good', mileage: '', hours: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setResult(null);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageAnalysis = async () => {
    if (!selectedImage) {
      toast.error('Please select an image first');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);
      
      const token = getAuthToken();
      const res = await axios.post(`${API}/tools/analyze-quote-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      setResult({ type: 'image', data: res.data.analysis });
      toast.success('Quote analyzed successfully!');
    } catch (err) {
      console.error('Image analysis error:', err);
      toast.error(err.response?.data?.detail || 'Failed to analyze image');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.asking_price || !form.rv_year) { 
      toast.error('Please fill in required fields'); 
      return; 
    }
    setLoading(true);
    try {
      const payload = {
        asking_price: parseFloat(form.asking_price),
        rv_year: parseInt(form.rv_year),
        rv_brand: form.rv_make || 'Unknown', 
        rv_model: form.rv_model || 'Unknown',
        condition: form.condition,
        mileage: form.mileage ? parseInt(form.mileage) : null,
      };
      const res = await axios.post(`${API}/tools/deal-analyzer`, payload);
      setResult({ type: 'manual', data: res.data });
    } catch (err) {
      console.error('Deal analyzer error:', err);
      toast.error(err.response?.data?.detail || 'Failed to analyze deal');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getVerdictColor = (verdict) => {
    const colors = {
      'GREAT_DEAL': 'bg-green-100 text-green-700 border-green-200',
      'GOOD_DEAL': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'FAIR': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'OVERPRICED': 'bg-orange-100 text-orange-700 border-orange-200',
      'BAD_DEAL': 'bg-red-100 text-red-700 border-red-200',
    };
    return colors[verdict] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-blue-500/20">
          <TrendingDown className="text-blue-500" size={28} />
        </div>
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            AI Deal Analyzer
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Analyze any RV deal - enter details or upload a quote screenshot
          </p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => { setMode('manual'); setResult(null); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            mode === 'manual' 
              ? 'bg-blue-600 text-white' 
              : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="mode-manual"
        >
          <Calculator size={16} />
          Enter Details
        </button>
        <button
          onClick={() => { setMode('image'); setResult(null); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            mode === 'image' 
              ? 'bg-blue-600 text-white' 
              : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="mode-image"
        >
          <Image size={16} />
          Upload Quote
        </button>
      </div>

      {/* Image Upload Mode */}
      {mode === 'image' && (
        <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
            id="quote-image-upload"
          />
          
          {!imagePreview ? (
            <label
              htmlFor="quote-image-upload"
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors ${
                isDark 
                  ? 'border-gray-600 hover:border-blue-500 hover:bg-gray-700/50' 
                  : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}
              data-testid="image-upload-zone"
            >
              <Upload className={`mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} size={40} />
              <p className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Upload a Quote Screenshot
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Drag and drop or click to select (PNG, JPG, WEBP)
              </p>
            </label>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Quote preview" 
                  className="max-h-96 w-auto mx-auto rounded-lg border"
                />
                <button
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  data-testid="remove-image"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex justify-center">
                <Button
                  onClick={handleImageAnalysis}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="analyze-image-btn"
                >
                  {loading ? (
                    <><Loader2 className="animate-spin mr-2" size={18} />Analyzing Quote...</>
                  ) : (
                    <><TrendingDown className="mr-2" size={18} />Analyze This Quote</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Mode */}
      {mode === 'manual' && (
        <form onSubmit={handleSubmit} className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Asking Price *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-3 text-gray-400" size={16} />
                <Input
                  type="number"
                  placeholder="45000"
                  value={form.asking_price}
                  onChange={(e) => setForm({...form, asking_price: e.target.value})}
                  className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                />
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Year *</Label>
              <Input
                type="number"
                placeholder="2022"
                value={form.rv_year}
                onChange={(e) => setForm({...form, rv_year: e.target.value})}
                className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Make</Label>
              <Input
                placeholder="Forest River"
                value={form.rv_make}
                onChange={(e) => setForm({...form, rv_make: e.target.value})}
                className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Model</Label>
              <Input
                placeholder="Cherokee"
                value={form.rv_model}
                onChange={(e) => setForm({...form, rv_model: e.target.value})}
                className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>RV Type</Label>
              <select
                value={form.rv_type}
                onChange={(e) => setForm({...form, rv_type: e.target.value})}
                className={`w-full mt-1 h-10 rounded-md border px-3 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              >
                <option value="travel_trailer">Travel Trailer</option>
                <option value="fifth_wheel">Fifth Wheel</option>
                <option value="motorhome_class_a">Class A</option>
                <option value="motorhome_class_b">Class B</option>
                <option value="motorhome_class_c">Class C</option>
              </select>
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Condition</Label>
              <select
                value={form.condition}
                onChange={(e) => setForm({...form, condition: e.target.value})}
                className={`w-full mt-1 h-10 rounded-md border px-3 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Mileage</Label>
              <Input
                type="number"
                placeholder="Optional"
                value={form.mileage}
                onChange={(e) => setForm({...form, mileage: e.target.value})}
                className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="mt-6 bg-blue-600 hover:bg-blue-700">
            {loading ? <><Loader2 className="animate-spin mr-2" size={18} />Analyzing...</> : 'Analyze Deal'}
          </Button>
        </form>
      )}

      {/* Image Analysis Results */}
      {result?.type === 'image' && result.data && (
        <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm space-y-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : ''}`}>Quote Analysis Results</h2>
              {result.data.deal_assessment?.verdict && (
                <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getVerdictColor(result.data.deal_assessment.verdict)}`}>
                  {result.data.deal_assessment.verdict.replace('_', ' ')}
                </span>
              )}
            </div>
            <button
              onClick={() => { setResult(null); handleRemoveImage(); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isDark 
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X size={14} />
              Clear
            </button>
          </div>

          {/* Extracted Info */}
          {result.data.extracted_info && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {result.data.extracted_info.rv_year && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className="text-xs text-gray-500">Year/Make/Model</p>
                  <p className={`font-bold ${isDark ? 'text-white' : ''}`}>
                    {result.data.extracted_info.rv_year} {result.data.extracted_info.rv_make} {result.data.extracted_info.rv_model}
                  </p>
                </div>
              )}
              {result.data.extracted_info.sale_price && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <p className="text-xs text-gray-500">Sale Price</p>
                  <p className="font-bold text-blue-600">${result.data.extracted_info.sale_price?.toLocaleString()}</p>
                </div>
              )}
              {result.data.out_the_door_price && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-50'}`}>
                  <p className="text-xs text-gray-500">Out-the-Door Price</p>
                  <p className="font-bold text-green-600">${result.data.out_the_door_price?.toLocaleString()}</p>
                </div>
              )}
              {result.data.suggested_counter_offer && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-purple-50'}`}>
                  <p className="text-xs text-gray-500">Suggested Counter</p>
                  <p className="font-bold text-purple-600">${result.data.suggested_counter_offer?.toLocaleString()}</p>
                </div>
              )}
            </div>
          )}

          {/* Hidden Fees */}
          {result.data.hidden_fees_found?.length > 0 && (
            <div>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <AlertTriangle className="text-amber-500" size={18} />
                Fee Analysis
              </h3>
              <div className="space-y-2">
                {result.data.hidden_fees_found.map((fee, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      fee.assessment === 'excessive' ? 'bg-red-50 border border-red-200' :
                      fee.assessment === 'high' ? 'bg-amber-50 border border-amber-200' :
                      isDark ? 'bg-gray-700' : 'bg-gray-50'
                    }`}
                  >
                    <span className={isDark && fee.assessment === 'fair' ? 'text-gray-300' : ''}>{fee.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">Typical: ${fee.typical_amount?.toLocaleString()}</span>
                      <span className={`font-bold ${
                        fee.assessment === 'excessive' ? 'text-red-600' :
                        fee.assessment === 'high' ? 'text-amber-600' : 'text-green-600'
                      }`}>
                        ${fee.your_amount?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Negotiation Tips */}
          {result.data.negotiation_tips?.length > 0 && (
            <div>
              <h3 className={`font-bold mb-3 ${isDark ? 'text-white' : ''}`}>Negotiation Tips</h3>
              <ul className="space-y-2">
                {result.data.negotiation_tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={16} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Summary */}
          {result.data.summary && (
            <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>{result.data.summary}</p>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Results */}
      {result?.type === 'manual' && result.data && (
        <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          {/* Header with Verdict */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : ''}`}>Analysis Results</h2>
              {result.data.analysis?.verdict && (
                <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getVerdictColor(result.data.analysis.verdict)}`}>
                  {result.data.analysis.verdict_emoji} {result.data.analysis.verdict.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            <button
              onClick={() => setResult(null)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isDark 
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X size={14} />
              Clear
            </button>
          </div>
          
          {/* Price Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className="text-sm text-gray-500">Asking Price</p>
              <p className="text-xl font-bold">${parseFloat(form.asking_price || 0).toLocaleString()}</p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-50'}`}>
              <p className="text-sm text-gray-500">Estimated Market Value</p>
              <p className="text-xl font-bold text-green-600">${result.data.analysis?.estimated_market_value?.toLocaleString() || '–'}</p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className="text-sm text-gray-500">Suggested Offer</p>
              <p className="text-xl font-bold text-blue-600">${result.data.analysis?.suggested_offer?.toLocaleString() || '–'}</p>
            </div>
          </div>
          
          {/* Price Difference */}
          {result.data.analysis?.price_difference && (
            <div className={`p-3 mb-4 rounded-lg flex items-center justify-between ${
              result.data.analysis.price_difference < 0 
                ? isDark ? 'bg-green-900/30' : 'bg-green-50' 
                : isDark ? 'bg-red-900/30' : 'bg-red-50'
            }`}>
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Price vs Market Value</span>
              <span className={`font-bold ${result.data.analysis.price_difference < 0 ? 'text-green-600' : 'text-red-600'}`}>
                {result.data.analysis.price_difference < 0 ? '−' : '+'}${Math.abs(result.data.analysis.price_difference).toLocaleString()} 
                ({result.data.analysis.price_difference_percent?.toFixed(1)}%)
              </span>
            </div>
          )}

          {/* Pros */}
          {result.data.analysis?.pros?.length > 0 && (
            <div className="mb-4">
              <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : ''}`}>✅ Pros</h3>
              <ul className="space-y-2">
                {result.data.analysis.pros.map((pro, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={16} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{pro}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Cons */}
          {result.data.analysis?.cons?.length > 0 && (
            <div className="mb-4">
              <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : ''}`}>⚠️ Cons</h3>
              <ul className="space-y-2">
                {result.data.analysis.cons.map((con, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <AlertTriangle className="text-orange-500 shrink-0 mt-0.5" size={16} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{con}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* What to Check */}
          {result.data.analysis?.what_to_check?.length > 0 && (
            <div className="mb-4">
              <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : ''}`}>🔍 What to Check</h3>
              <ul className="space-y-2">
                {result.data.analysis.what_to_check.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Search className="text-blue-500 shrink-0 mt-0.5" size={16} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Summary */}
          {result.data.analysis?.summary && (
            <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Summary</p>
              <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>{result.data.analysis.summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Negotiation Coach for Dealers
 */
export const NegotiationCoachEmbed = ({ isDark }) => {
  const [form, setForm] = useState({
    asking_price: '', target_price: '', rv_year: '', rv_make: '',
    rv_model: '', condition: 'good', days_on_market: '', seller_motivation: 'normal',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.asking_price || !form.rv_year) {
      toast.error('Please fill in required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        listing_price: parseFloat(form.asking_price),
        rv_year: parseInt(form.rv_year),
        rv_brand: form.rv_make || 'Unknown',
        rv_model: form.rv_model || 'Unknown',
        condition: form.condition,
      };
      const res = await axios.post(`${API}/tools/negotiation-coach`, payload);
      setResult(res.data);
    } catch (err) {
      console.error('Negotiation coach error:', err);
      toast.error(err.response?.data?.detail || 'Failed to get negotiation strategy');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-purple-500/20">
          <Gavel className="text-purple-500" size={28} />
        </div>
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            AI Negotiation Coach
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Get personalized negotiation strategies and scripts
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Asking Price *</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-3 text-gray-400" size={16} />
              <Input
                type="number"
                placeholder="50000"
                value={form.asking_price}
                onChange={(e) => setForm({...form, asking_price: e.target.value})}
                className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
            </div>
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Your Target Price *</Label>
            <div className="relative mt-1">
              <Target className="absolute left-3 top-3 text-gray-400" size={16} />
              <Input
                type="number"
                placeholder="42000"
                value={form.target_price}
                onChange={(e) => setForm({...form, target_price: e.target.value})}
                className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
            </div>
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>RV Year</Label>
            <Input
              type="number"
              placeholder="2022"
              value={form.rv_year}
              onChange={(e) => setForm({...form, rv_year: e.target.value})}
              className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Days on Market</Label>
            <Input
              type="number"
              placeholder="30"
              value={form.days_on_market}
              onChange={(e) => setForm({...form, days_on_market: e.target.value})}
              className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Make</Label>
            <Input
              placeholder="Forest River"
              value={form.rv_make}
              onChange={(e) => setForm({...form, rv_make: e.target.value})}
              className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Model</Label>
            <Input
              placeholder="Cherokee"
              value={form.rv_model}
              onChange={(e) => setForm({...form, rv_model: e.target.value})}
              className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Condition</Label>
            <select
              value={form.condition}
              onChange={(e) => setForm({...form, condition: e.target.value})}
              className={`w-full mt-1 h-10 rounded-md border px-3 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
            >
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
            </select>
          </div>
          <div>
            <Label className={isDark ? 'text-gray-300' : ''}>Seller Motivation</Label>
            <select
              value={form.seller_motivation}
              onChange={(e) => setForm({...form, seller_motivation: e.target.value})}
              className={`w-full mt-1 h-10 rounded-md border px-3 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
            >
              <option value="low">Low (Firm on price)</option>
              <option value="normal">Normal</option>
              <option value="high">High (Motivated seller)</option>
            </select>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="mt-6 bg-purple-600 hover:bg-purple-700">
          {loading ? <><Loader2 className="animate-spin mr-2" size={18} />Generating Strategy...</> : 'Get Negotiation Strategy'}
        </Button>
      </form>

      {result && (
        <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm space-y-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : ''}`}>Your Negotiation Strategy</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                result.success_probability >= 70 ? 'bg-green-100 text-green-700' :
                result.success_probability >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
              }`}>
                {result.success_probability}% Success Probability
              </span>
            </div>
            <button
              onClick={() => setResult(null)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isDark 
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X size={14} />
              Clear
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className="text-sm text-gray-500">Recommended Opening Offer</p>
              <p className="text-2xl font-bold text-green-600">${result.recommended_opening_offer?.toLocaleString()}</p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className="text-sm text-gray-500">Expected Final Price</p>
              <p className="text-2xl font-bold text-blue-600">${result.expected_final_price?.toLocaleString()}</p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-purple-50'}`}>
              <p className="text-sm text-gray-500">Potential Savings</p>
              <p className="text-2xl font-bold text-purple-600">${result.potential_savings?.toLocaleString()}</p>
            </div>
          </div>

          {result.opening_script && (
            <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${isDark ? 'bg-gray-700' : 'bg-purple-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="text-purple-500" size={18} />
                <h3 className={`font-bold ${isDark ? 'text-white' : ''}`}>Opening Script</h3>
              </div>
              <p className={`italic ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>"{result.opening_script}"</p>
            </div>
          )}

          {result.key_points && result.key_points.length > 0 && (
            <div>
              <h3 className={`font-bold mb-3 ${isDark ? 'text-white' : ''}`}>Key Talking Points</h3>
              <ul className="space-y-2">
                {result.key_points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={16} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
